/**
 * Tiny typed hash router — pure part (no DOM): `Route` union, `parseHash`, `formatRoute`.
 * The DOM binding (`useRoute`, `navigate`) lives in navigation.ts.
 *
 *   #/                                   home
 *   #/new                                new-game wizard
 *   #/play?persona=&tc=&color=&coach=    live game; coach = teacher | helper | exam (docs/TEACHER-MODE.md §1.3)
 *                                        (an old link's `exam=1` still means coach=exam; nothing = helper, the old behaviour)
 *   #/play?path=<stepId>                 a path game started from the map (PATH-V2 §12.4): the bot, the help, the colour
 *                                        come from the step and the stored path games (features/path/pathGame.ts)
 *   #/review/:id                         post-game review
 *   #/puzzles[?theme=][&warmup=1]        puzzles (warmup = the three-puzzle «разминка» of the today plan)
 *   #/progress                           progress dashboard
 *   #/path                               curriculum «Путь пешки» (the 2.0 map when the feature 'path-v2' is on)
 *   #/path/lesson/:stepId                a step of the path 2.0 (the lesson screen; a placeholder for now)
 *   #/path/parent | #/path/placement      «Для взрослых» of the path 2.0 (behind the parent gate) | the placement test
 *   #/settings                           settings
 *   #/playground[?tool=ui|stage|minigame] dev harnesses (lazy-loaded; stage = the path StageBoard demo, minigame = «Собери подарки»)
 *
 * Anything that does not parse falls back to a safe route (home, or the wizard for a broken
 * #/play link) — a child must never land on a dead end.
 */
import { coachStylesFor, defaultCoachStyle } from '@gambit/core';
import { pathGameStep } from '@gambit/content';
import { PERSONA_IDS, TIME_CONTROL_IDS } from '@gambit/shared';
import type { CoachStyle, Color, PersonaId, TimeControlId } from '@gambit/shared';

export type PlaygroundTool = 'mascot' | 'ui' | 'stage' | 'minigame';

export type Route =
  | { name: 'home' }
  | { name: 'new' }
  /** §12.4 the production preview of a step (feature 'preview'): `task` = start at this task key, `seed` = a generated step's seed */
  | { name: 'pathPreview'; stepId: string; task?: string; seed?: number; autoplay?: boolean }
  | {
      name: 'play';
      personaId: PersonaId;
      timeControlId: TimeControlId;
      childColor: Color;
      /** how Гамбитик helps in this game; always one the time control offers (see `allowedCoachStyle`) */
      coachStyle: CoachStyle;
      /** derived: `coachStyle === 'exam'` (kept for the GameScreen prop and older readers) */
      examMode: boolean;
      /** a path game: the step on the map (the game's setup is decided when it starts) */
      pathStepId?: string;
    }
  | { name: 'review'; gameId: string }
  | { name: 'puzzles'; theme?: string; warmup?: boolean }
  | { name: 'progress' }
  | { name: 'path'; view?: 'parent' | 'placement' }
  | { name: 'pathLesson'; stepId: string }
  | { name: 'settings' }
  | { name: 'playground'; tool: PlaygroundTool };

export type RouteName = Route['name'];
export type PlayRoute = Extract<Route, { name: 'play' }>;

export const HOME_ROUTE: Route = { name: 'home' };

// ───────────────────────── coach style (docs/TEACHER-MODE.md §1.2–§1.3) ─────────────────────────

export const COACH_STYLES: readonly CoachStyle[] = ['teacher', 'helper', 'exam', 'quiet'];

/**
 * The stage a link or a snapshot without a known stage is judged at when its style is not offered: 5, explicitly
 * (docs/TEACHING-V3.md §2.10 — it was TEACHER_DEFAULT_MAX_STAGE + 1 while that was 4; now that the teacher is the default
 * up to stage 5 the router's «unknown» stays 5 and no longer follows the constant). With today's table every such
 * fallback is 'helper' anyway: training, 10 and 5 minutes offer all three styles, and bullet offers none.
 */
export const ROUTER_UNKNOWN_STAGE = 5;
const UNKNOWN_STAGE = ROUTER_UNKNOWN_STAGE;

export function isCoachStyle(value: unknown): value is CoachStyle {
  return COACH_STYLES.some((style) => style === value);
}

/**
 * The style a game with this time control really runs: an offered style stays, anything else becomes
 * `defaultCoachStyle(tc, stage)`. Bullet offers none — the coach is silent there — and gets the harmless 'helper'
 * (never 'exam': a one-minute game was never an exam, so the old links and records keep `examMode: false`).
 */
export function allowedCoachStyle(timeControlId: TimeControlId, style: CoachStyle, stage: number = UNKNOWN_STAGE): CoachStyle {
  // «тихий учитель» (PATH-V2 §7.1, the path's ladder) where «Учитель» is offered; the game drops it when the path is off
  if (style === 'quiet' && coachStylesFor(timeControlId).includes('teacher')) return style;
  return coachStylesFor(timeControlId).includes(style) ? style : defaultCoachStyle(timeControlId, stage);
}

/** A #/play route with a checked style and `examMode` derived from it. */
export function playRoute(a: { personaId: PersonaId; timeControlId: TimeControlId; childColor: Color; coachStyle: CoachStyle; stage?: number }): PlayRoute {
  const coachStyle = allowedCoachStyle(a.timeControlId, a.coachStyle, a.stage);
  return { name: 'play', personaId: a.personaId, timeControlId: a.timeControlId, childColor: a.childColor, coachStyle, examMode: coachStyle === 'exam' };
}

/**
 * A route with «Путь пешки» 2.0 OFF means what it meant before the path (fixer r1): `#/play?path=<step>` → the game
 * wizard (#/new) — not an ordinary game against the step's bot; `coach=quiet` («тихий учитель» is the path's ladder) →
 * «Подсказчик», what an unknown `coach=` gave before the path, so the address bar reads `coach=helper` again. Anything
 * else is itself.
 */
export function pathOffRoute(route: Route): Route {
  if (route.name !== 'play') return route;
  if (route.pathStepId !== undefined) return { name: 'new' };
  if (route.coachStyle !== 'quiet') return route;
  return playRoute({ personaId: route.personaId, timeControlId: route.timeControlId, childColor: route.childColor, coachStyle: 'helper' });
}

/**
 * The #/play route of a path game step, or null when `stepId` is not a game step of the path. No clock ('training');
 * the bot is the step's; colour and coach style here are placeholders — the game's `GameConfig.path` decides them.
 */
export function pathPlayRoute(stepId: string): PlayRoute | null {
  if (!STEP_ID_PATTERN.test(stepId)) return null;
  // the small table of the road's game steps — the router never loads the chapters (fixer r1)
  const step = pathGameStep(stepId);
  if (step === undefined) return null;
  const exam = step.exam;
  return { name: 'play', personaId: step.persona, timeControlId: 'training', childColor: step.color ?? 'w', coachStyle: exam ? 'exam' : 'helper', examMode: exam, pathStepId: stepId };
}

/** Where a step of the map leads: a game step → its #/play?path= game, every other step → #/path/lesson/<stepId>. */
export function pathStepRoute(stepId: string): Route {
  return pathPlayRoute(stepId) ?? { name: 'pathLesson', stepId };
}

/** `coach=` wins; an old link's `exam=1` means «Экзамен»; nothing (or junk) means «Подсказчик», the behaviour before teacher mode. */
function coachStyleFromQuery(query: URLSearchParams): CoachStyle {
  const coach = query.get('coach');
  if (isCoachStyle(coach)) return coach;
  return query.get('exam') === '1' ? 'exam' : 'helper';
}

/** Same rule as the server's `?theme=` query validation: a lichess theme key. */
const THEME_PATTERN = /^[A-Za-z0-9]{1,40}$/;
const MAX_GAME_ID_LENGTH = 128;
/** a path step id: 'gifts-intro', 'gifts-minigame' … */
const STEP_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;

function isPersonaId(value: string | null): value is PersonaId {
  return value !== null && PERSONA_IDS.some((id) => id === value);
}

function isTimeControlId(value: string | null): value is TimeControlId {
  return value !== null && TIME_CONTROL_IDS.some((id) => id === value);
}

function safeDecode(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

/** Returns a valid theme key or undefined (the puzzles screen then serves the adaptive mix). */
export function normalizeTheme(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined) return undefined;
  return THEME_PATTERN.test(value) ? value : undefined;
}

/** Accepts `location.hash` in any shape: '', '#', '#/', '#/play?…', '/play', 'play'. */
export function parseHash(hash: string): Route {
  let rest = hash.startsWith('#') ? hash.slice(1) : hash;
  const queryStart = rest.indexOf('?');
  const query = new URLSearchParams(queryStart === -1 ? '' : rest.slice(queryStart + 1));
  if (queryStart !== -1) rest = rest.slice(0, queryStart);
  const segments = rest.split('/').filter((segment) => segment !== '');
  const [head, second, ...extra] = segments;

  if (head === undefined) return HOME_ROUTE;

  switch (head) {
    case 'new':
      return second === undefined ? { name: 'new' } : HOME_ROUTE;

    case 'play': {
      if (second !== undefined) return HOME_ROUTE;
      const pathStep = query.get('path');
      // a path game: its step decides; a step that is not (or no longer) a game goes back to the map
      if (pathStep !== null) return pathPlayRoute(pathStep) ?? { name: 'path' };
      const persona = query.get('persona');
      const timeControl = query.get('tc');
      // a broken or hand-edited link: let the child choose again instead of guessing
      if (!isPersonaId(persona) || !isTimeControlId(timeControl)) return { name: 'new' };
      return playRoute({
        personaId: persona,
        timeControlId: timeControl,
        childColor: query.get('color') === 'b' ? 'b' : 'w',
        coachStyle: coachStyleFromQuery(query),
      });
    }

    case 'review': {
      if (second === undefined || extra.length > 0) return HOME_ROUTE;
      const gameId = safeDecode(second);
      if (gameId === null || gameId.trim() === '' || gameId.length > MAX_GAME_ID_LENGTH) return HOME_ROUTE;
      return { name: 'review', gameId };
    }

    case 'puzzles': {
      if (second !== undefined) return HOME_ROUTE;
      const theme = normalizeTheme(query.get('theme'));
      return { name: 'puzzles', ...(theme === undefined ? {} : { theme }), ...(query.get('warmup') === '1' ? { warmup: true } : {}) };
    }

    case 'progress':
      return second === undefined ? { name: 'progress' } : HOME_ROUTE;

    case 'path': {
      if (second === undefined) return { name: 'path' };
      if ((second === 'parent' || second === 'placement') && extra.length === 0) return { name: 'path', view: second };
      // #/path/preview/<stepId>?task=<key>&seed=<n>&autoplay=1 (the App shows it only with the feature 'preview')
      if (second === 'preview' && extra.length === 1) {
        const stepId = safeDecode(extra[0] ?? '');
        if (stepId === null || !STEP_ID_PATTERN.test(stepId)) return { name: 'path' };
        const task = query.get('task');
        const seed = Number(query.get('seed'));
        return {
          name: 'pathPreview',
          stepId,
          ...(task !== null && /^[\w./:@#-]{1,120}$/u.test(task) ? { task } : {}),
          ...(Number.isInteger(seed) && seed >= 0 && seed <= 0xffffffff && query.get('seed') !== null ? { seed } : {}),
          ...(query.get('autoplay') === '1' ? { autoplay: true } : {}),
        };
      }
      // #/path/lesson/<stepId>: a semantic step id (docs/PATH-V2.md §12.1 p.2); anything else goes back to the map
      if (second !== 'lesson' || extra.length !== 1) return { name: 'path' };
      const stepId = safeDecode(extra[0] ?? '');
      return stepId !== null && STEP_ID_PATTERN.test(stepId) ? { name: 'pathLesson', stepId } : { name: 'path' };
    }

    case 'settings':
      return second === undefined ? { name: 'settings' } : HOME_ROUTE;

    case 'playground': {
      if (second !== undefined) return HOME_ROUTE;
      const tool = query.get('tool');
      return { name: 'playground', tool: tool === 'ui' || tool === 'stage' || tool === 'minigame' ? tool : 'mascot' };
    }

    default:
      return HOME_ROUTE;
  }
}

/** Canonical hash of a route; `parseHash(formatRoute(r))` deep-equals `r` for every valid route. */
export function formatRoute(route: Route): string {
  switch (route.name) {
    case 'home':
      return '#/';
    case 'new':
      return '#/new';
    case 'play': {
      if (route.pathStepId !== undefined) return `#/play?path=${encodeURIComponent(route.pathStepId)}`;
      const query = new URLSearchParams({
        persona: route.personaId,
        tc: route.timeControlId,
        color: route.childColor,
        coach: allowedCoachStyle(route.timeControlId, route.coachStyle),
      });
      return `#/play?${query.toString()}`;
    }
    case 'review':
      return `#/review/${encodeURIComponent(route.gameId)}`;
    case 'puzzles': {
      const query = new URLSearchParams();
      const theme = normalizeTheme(route.theme);
      if (theme !== undefined) query.set('theme', theme);
      if (route.warmup === true) query.set('warmup', '1');
      const search = query.toString();
      return search === '' ? '#/puzzles' : `#/puzzles?${search}`;
    }
    case 'progress':
      return '#/progress';
    case 'path':
      return route.view ? `#/path/${route.view}` : '#/path';
    case 'pathLesson':
      return `#/path/lesson/${encodeURIComponent(route.stepId)}`;
    case 'pathPreview': {
      const q = new URLSearchParams();
      if (route.task !== undefined) q.set('task', route.task);
      if (route.seed !== undefined) q.set('seed', String(route.seed));
      if (route.autoplay) q.set('autoplay', '1');
      const query = q.toString();
      return `#/path/preview/${encodeURIComponent(route.stepId)}${query ? `?${query}` : ''}`;
    }
    case 'settings':
      return '#/settings';
    case 'playground':
      return route.tool === 'mascot' ? '#/playground' : `#/playground?tool=${route.tool}`;
  }
}

/** True when both routes show the same screen with the same parameters. */
export function sameRoute(a: Route, b: Route): boolean {
  return formatRoute(a) === formatRoute(b);
}
